import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { preferencesAPI, EffectivePreferences, PreferencesChoices, UserPreferences, coreAPI } from '../lib/api';
import { formatCompactCurrencyNumber } from '../lib/utils';
import { localeForCurrency, usesIndianGrouping, groupIndian } from '../utils/currencyLocale';
import { useAuth } from './AuthContext';
import { useView } from './ViewContext';

// Exchange rates type
interface ExchangeRates {
  base_currency: string;
  rates: Record<string, string>;
  as_of: string;
}

// Platform base currency (all backend values are in this currency)
const PLATFORM_BASE_CURRENCY = 'GBP';

// Platform defaults
const PLATFORM_DEFAULTS: EffectivePreferences = {
  currency: 'GBP',
  currency_display: 'British Pound (GBP)',
  timezone: 'UTC',
  timezone_display: 'UTC',
  date_format: 'DD/MM/YYYY',
  date_format_display: 'DD/MM/YYYY (UK Format)',
  language: 'en-GB',
  language_display: 'English (UK)',
  decimal_separator: '.',
  thousands_separator: ',',
  // CR-4707: Platform-default decimal precision for unit-price fields in the
  // PO Line pop-up. Overridable per-company from Admin Setup → Regional.
  unit_price_decimals: 2,
  // OTIF/KPI percentage decimals — whole numbers unless the admin opts in.
  kpi_percent_decimals: 0,
  default_site_id: null,
  default_site_name: null,
  sources: {
    currency: 'platform',
    timezone: 'platform',
    date_format: 'platform',
    language: 'platform',
    decimal_separator: 'platform',
    thousands_separator: 'platform',
  },
};

interface PreferencesContextType {
  // The effective (computed) preferences with cascade applied
  preferences: EffectivePreferences;
  // Raw user preferences (for editing)
  userPreferences: UserPreferences | null;
  // Available choices for dropdowns
  choices: PreferencesChoices | null;
  // Exchange rates for currency conversion
  exchangeRates: ExchangeRates | null;
  // Loading states
  isLoading: boolean;
  isUpdating: boolean;
  // Error state
  error: string | null;
  // Actions
  loadPreferences: () => Promise<void>;
  updateUserPreferences: (data: Partial<UserPreferences>) => Promise<void>;
  // Helper functions for formatting
  formatCurrency: (value: number, currency?: string, showDecimals?: boolean | number) => string;
  /**
   * Symbol + compact magnitude for chart axes and tiles, in the currency's own
   * convention: "£3.1M" / "$250K" for Western currencies, "₹3.4Cr" / "₹1.2L" for INR.
   * @param value - The amount in `currency` (no conversion)
   * @param currency - Optional currency code (defaults to the user's preference)
   */
  formatCompactCurrency: (value: number, currency?: string) => string;
  /**
   * Convert a stored "converted" value into the user's preferred currency and format it.
   * Backend converted_* figures are held in the CUSTOMER's reporting currency (its
   * base currency — INR for an INR-based tenant), not always GBP; pass that code as
   * `fromCurrency` (e.g. `line.converted_currency`). When no rate exists to reach the
   * target currency the value is formatted in `fromCurrency` rather than mislabelled.
   * @param valueInBaseCurrency - The value in the reporting (from) currency
   * @param targetCurrency - Optional target currency (defaults to user's preference)
   * @param fromCurrency - Currency the value is held in (defaults to the platform base, GBP)
   */
  formatCurrencyFromBase: (valueInBaseCurrency: number, targetCurrency?: string, fromCurrency?: string) => string;
  /**
   * Convert a value from a reporting currency to target currency without formatting.
   * @param valueInBaseCurrency - The value in the reporting (from) currency
   * @param targetCurrency - Optional target currency (defaults to user's preference)
   * @param fromCurrency - Currency the value is held in (defaults to the platform base, GBP)
   * @returns The converted numeric value (unchanged when no rate exists)
   */
  convertFromBase: (valueInBaseCurrency: number, targetCurrency?: string, fromCurrency?: string) => number;
  formatDate: (date: string | Date, options?: { format?: string; includeTime?: boolean }) => string;
  formatDateTime: (date: string | Date) => string;
  formatNumber: (value: number, showDecimals?: boolean) => string;
  /** CR-7262: quantities, which carry up to 3 decimals. See the impl. */
  formatQuantity: (value: number | string | null | undefined) => string;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

/**
 * Provides user/site/company-level preferences with a cascade hierarchy.
 * Handles currency conversion from the platform base currency (GBP), date/number formatting,
 * and reloads preferences automatically when the view context changes.
 */
export const PreferencesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewLoading } = useView();

  const [preferences, setPreferences] = useState<EffectivePreferences>(PLATFORM_DEFAULTS);
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);
  const [choices, setChoices] = useState<PreferencesChoices | null>(null);
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates | null>(null);
  // True until the first load for this session has completed (or until we
  // know there is nothing to load — unauthenticated). Consumers that gate
  // their first fetch on this (the dashboard) used to see `false` before the
  // load had even started and went out with platform-default GBP, then
  // refetched when the tenant's INR arrived.
  const [isLoading, setIsLoading] = useState(true);
  // Whether the first load for this authenticated session has completed.
  // `isLoading` alone has a gap: it is false while unauthenticated, and on
  // login it stays false until ViewContext resolves and the load actually
  // starts — so a consumer that gates on it could fetch in that window with
  // platform defaults. The exported flag ORs this in (see the value below).
  const [hasLoadedForSession, setHasLoadedForSession] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track previous view context to detect changes
  const prevViewRef = useRef<{ site: number | null; company: number | null; group: number | null }>({
    site: null,
    company: null,
    group: null,
  });

  // The view scope is read through a ref at FETCH time so loadViewPreferences
  // (and loadPreferences, which wraps it) keep a stable identity. Keying them
  // on the raw ids made the initial-load effect below re-run the WHOLE
  // preferences load (user prefs, choices, view-effective, exchange rates)
  // every time ViewContext back-filled an id — a second full round of
  // requests on every page load, the first of which was unscoped anyway.
  const viewScopeRef = useRef({ viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId });
  viewScopeRef.current = { viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId };

  // Load view-based effective preferences based on current view context
  const loadViewPreferences = useCallback(async () => {
    if (!isAuthenticated) {
      setPreferences(PLATFORM_DEFAULTS);
      return;
    }

    setError(null);

    try {
      // Determine which context to use based on view level
      const params: { site?: number | null; company?: number | null; group?: number | null } = {};
      const { viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId } = viewScopeRef.current;

      switch (viewLevel) {
        case 'SITE':
          if (selectedSiteId) params.site = selectedSiteId;
          break;
        case 'COMPANY':
          if (selectedCompanyId) params.company = selectedCompanyId;
          break;
        case 'GROUP':
          if (selectedGroupId) params.group = selectedGroupId;
          break;
      }

      const effectiveRes = await preferencesAPI.getViewEffectivePreferences(params);
      setPreferences(effectiveRes.data);
    } catch (err) {
      console.error('Failed to load view preferences:', err);
      // Fall back to user preferences on error
      try {
        const effectiveRes = await preferencesAPI.getEffectivePreferences();
        setPreferences(effectiveRes.data);
      } catch {
        setPreferences(PLATFORM_DEFAULTS);
      }
    }
  }, [isAuthenticated]);

  // Load exchange rates
  const loadExchangeRates = useCallback(async () => {
    try {
      const response = await coreAPI.getLatestExchangeRates();
      setExchangeRates(response.data);
    } catch (err) {
      console.error('Failed to load exchange rates:', err);
      // Set fallback rates if API fails
      setExchangeRates({
        base_currency: PLATFORM_BASE_CURRENCY,
        rates: {
          GBP: '1.000000',
          USD: '1.27',
          EUR: '1.17',
          JPY: '190.00',
          CNY: '9.20',
          INR: '106.00',
          AUD: '1.95',
          CAD: '1.73',
          CHF: '1.12',
          MXN: '21.70',
        },
        as_of: new Date().toISOString().split('T')[0],
      });
    }
  }, []);

  // Load initial data (choices and user preferences for reference)
  const loadPreferences = useCallback(async () => {
    if (!isAuthenticated) {
      setPreferences(PLATFORM_DEFAULTS);
      setUserPreferences(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Load choices, user preferences, and exchange rates
      const [userRes, choicesRes] = await Promise.all([
        preferencesAPI.getUserPreferences(),
        preferencesAPI.getPreferencesChoices(),
      ]);

      setUserPreferences(userRes.data);
      setChoices(choicesRes.data);

      // Load exchange rates in parallel
      await Promise.all([
        loadViewPreferences(),
        loadExchangeRates(),
      ]);
    } catch (err) {
      console.error('Failed to load preferences:', err);
      setError('Failed to load preferences');
      setPreferences(PLATFORM_DEFAULTS);
    } finally {
      setIsLoading(false);
      setHasLoadedForSession(true);
    }
  }, [isAuthenticated, loadViewPreferences, loadExchangeRates]);

  const updateUserPreferences = useCallback(async (data: Partial<UserPreferences>) => {
    setIsUpdating(true);
    setError(null);

    try {
      const response = await preferencesAPI.updateUserPreferences(data);
      setUserPreferences(response.data);

      // Reload view-based effective preferences
      await loadViewPreferences();
    } catch (err) {
      console.error('Failed to update preferences:', err);
      setError('Failed to save preferences');
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [loadViewPreferences]);

  // Initial load — once per authenticated session, and only after ViewContext
  // has resolved the active Group/Division/Site so the first view-effective
  // request already carries the right scope (it used to fire unscoped, then
  // again scoped). Subsequent scope changes are handled by the effect below.
  const hasLoadedForSessionRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated) {
      hasLoadedForSessionRef.current = false;
      setHasLoadedForSession(false);
      setPreferences(PLATFORM_DEFAULTS);
      setUserPreferences(null);
      setChoices(null);
      setIsLoading(false);
      return;
    }
    if (viewLoading || hasLoadedForSessionRef.current) return;
    hasLoadedForSessionRef.current = true;
    // The scope this load reads is the baseline the change-detector compares
    // against — otherwise the resolved ids would read as a "change" and
    // trigger a second view-effective request straight after this one.
    prevViewRef.current = { site: selectedSiteId, company: selectedCompanyId, group: selectedGroupId };
    loadPreferences();
  }, [isAuthenticated, viewLoading, loadPreferences, selectedSiteId, selectedCompanyId, selectedGroupId]);

  // Reload preferences when view context changes (but not on initial load)
  useEffect(() => {
    const prevView = prevViewRef.current;
    const viewChanged = (
      prevView.site !== selectedSiteId ||
      prevView.company !== selectedCompanyId ||
      prevView.group !== selectedGroupId
    );

    // Update ref for next comparison
    prevViewRef.current = {
      site: selectedSiteId,
      company: selectedCompanyId,
      group: selectedGroupId,
    };

    // Only reload if view actually changed (not on initial mount) and view is loaded
    if (viewChanged && isAuthenticated && !viewLoading && (prevView.site !== null || prevView.company !== null || prevView.group !== null)) {
      loadViewPreferences();
    }
  }, [selectedSiteId, selectedCompanyId, selectedGroupId, isAuthenticated, viewLoading, loadViewPreferences]);

  // Helper: Format currency value (no conversion - value is assumed to be in the specified currency)
  const formatCurrency = useCallback((value: number, currency?: string, showDecimals: boolean | number = false): string => {
    const currencyCode = currency || preferences.currency;
    const decimals = typeof showDecimals === 'number' ? showDecimals : showDecimals ? 3 : 0;
    // Grouping follows the currency: INR groups in lakhs/crores (en-IN),
    // everything else keeps en-GB exactly as before. Symbol and decimals come
    // from the currency code either way.
    const locale = localeForCurrency(currencyCode);
    // Zero (and nullish/NaN) values render without a currency symbol — "£0.00"
    // is visually noisy on dashboards, lists, and exports. Show plain "0".
    if (!value) {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(0);
    }
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
    // CR-4351: Intl formats USD as "US$" in en-GB locale — normalize to "$"
    return formatted.replace('US$', '$');
  }, [preferences.currency]);

  // Helper: symbol + compact magnitude for axes/tiles ("₹3.4Cr", "£3.1M").
  const formatCompactCurrency = useCallback((value: number, currency?: string): string => {
    const currencyCode = currency || preferences.currency;
    const locale = localeForCurrency(currencyCode);
    let symbol = '';
    try {
      symbol = new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode })
        .formatToParts(1).find(p => p.type === 'currency')?.value ?? '';
    } catch {
      symbol = '';
    }
    symbol = symbol.replace('US$', '$');
    return `${symbol}${formatCompactCurrencyNumber(value, currencyCode)}`;
  }, [preferences.currency]);

  // Rates from the API are "1 GBP = X currency"; GBP itself is 1. Returns null
  // when the currency has no rate, so callers can decide not to convert.
  const gbpRateFor = useCallback((code: string): number | null => {
    if (code === PLATFORM_BASE_CURRENCY) return 1;
    const rateStr = exchangeRates?.rates[code];
    const rate = rateStr ? parseFloat(rateStr) : NaN;
    return isNaN(rate) || rate === 0 ? null : rate;
  }, [exchangeRates]);

  // Helper: Convert a value held in `fromCurrency` (the customer's reporting
  // currency; platform base GBP when not given) into the target currency.
  const convertFromBase = useCallback((valueInBaseCurrency: number, targetCurrency?: string, fromCurrency?: string): number => {
    const targetCode = targetCurrency || preferences.currency;
    const fromCode = fromCurrency || PLATFORM_BASE_CURRENCY;

    // Same currency, or no rates loaded yet — nothing to convert
    if (targetCode === fromCode || !exchangeRates) {
      return valueInBaseCurrency;
    }

    const fromRate = gbpRateFor(fromCode);
    const targetRate = gbpRateFor(targetCode);
    if (fromRate === null || targetRate === null) {
      console.warn(`No exchange rate to convert ${fromCode} to ${targetCode}, returning unconverted value`);
      return valueInBaseCurrency;
    }

    // value / (1 GBP = X from) * (1 GBP = Y target)
    return (valueInBaseCurrency / fromRate) * targetRate;
  }, [preferences.currency, exchangeRates, gbpRateFor]);

  // Helper: Convert from the reporting currency and format. When the rate to
  // reach the target is missing, format in the reporting currency instead of
  // putting the wrong symbol on an unconverted number.
  const formatCurrencyFromBase = useCallback((valueInBaseCurrency: number, targetCurrency?: string, fromCurrency?: string): string => {
    const targetCode = targetCurrency || preferences.currency;
    const fromCode = fromCurrency || PLATFORM_BASE_CURRENCY;
    if (targetCode !== fromCode && (gbpRateFor(fromCode) === null || gbpRateFor(targetCode) === null)) {
      return formatCurrency(valueInBaseCurrency, fromCode);
    }
    const convertedValue = convertFromBase(valueInBaseCurrency, targetCode, fromCode);
    return formatCurrency(convertedValue, targetCode);
  }, [convertFromBase, formatCurrency, gbpRateFor, preferences.currency]);

  // Helper: Convert date to user's timezone
  const convertToUserTimezone = useCallback((date: Date): Date => {
    // Use Intl.DateTimeFormat to convert the date to user's timezone
    const timezone = preferences.timezone;
    try {
      // Get the date parts in the target timezone
      const options: Intl.DateTimeFormatOptions = {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      };
      const formatter = new Intl.DateTimeFormat('en-CA', options);
      const parts = formatter.formatToParts(date);

      const getPart = (type: string) => parts.find(p => p.type === type)?.value || '0';

      return new Date(
        parseInt(getPart('year')),
        parseInt(getPart('month')) - 1,
        parseInt(getPart('day')),
        parseInt(getPart('hour')),
        parseInt(getPart('minute')),
        parseInt(getPart('second'))
      );
    } catch {
      // If timezone is invalid, return original date
      return date;
    }
  }, [preferences.timezone]);

  // Helper: Format date according to user preferences (timezone-aware)
  const formatDate = useCallback((date: string | Date, options?: { format?: string; includeTime?: boolean }): string => {
    // Date-only strings ("YYYY-MM-DD") must NOT go through timezone conversion
    // because new Date("2025-12-30") is parsed as UTC midnight, and converting
    // to a local timezone behind UTC shifts the date back by one day.
    const isDateOnly = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date);
    let dateObj: Date;
    if (isDateOnly) {
      const [y, m, d] = (date as string).split('-').map(Number);
      dateObj = new Date(y, m - 1, d); // local time, no TZ shift
    } else {
      const inputDate = typeof date === 'string' ? new Date(date) : date;
      // Convert to user's timezone
      dateObj = convertToUserTimezone(inputDate);
    }
    const dateFormat = options?.format || preferences.date_format;

    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = monthNames[dateObj.getMonth()];

    let formattedDate: string;
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        formattedDate = `${day}/${month}/${year}`;
        break;
      case 'MM/DD/YYYY':
        formattedDate = `${month}/${day}/${year}`;
        break;
      case 'YYYY-MM-DD':
        formattedDate = `${year}-${month}-${day}`;
        break;
      case 'DD-MMM-YYYY':
        formattedDate = `${day}-${monthShort}-${year}`;
        break;
      default:
        formattedDate = `${day}/${month}/${year}`;
    }

    if (options?.includeTime) {
      const hours = dateObj.getHours().toString().padStart(2, '0');
      const minutes = dateObj.getMinutes().toString().padStart(2, '0');
      formattedDate += ` ${hours}:${minutes}`;
    }

    return formattedDate;
  }, [preferences.date_format, convertToUserTimezone]);

  // Helper: Format date and time according to user preferences
  const formatDateTime = useCallback((date: string | Date): string => {
    return formatDate(date, { includeTime: true });
  }, [formatDate]);

  // Plain numbers sit beside currency figures, so they group the same way:
  // lakhs/crores when the effective currency is INR, thousands otherwise.
  // Shared by formatNumber and formatQuantity so the two cannot drift.
  const group = useCallback((digits: string): string => (
    usesIndianGrouping(preferences.currency)
      ? groupIndian(digits, preferences.thousands_separator)
      : digits.replace(/\B(?=(\d{3})+(?!\d))/g, preferences.thousands_separator)
  ), [preferences.currency, preferences.thousands_separator]);

  // Helper: Format number with thousands separator
  const formatNumber = useCallback((value: number, showDecimals: boolean = false): string => {
    if (showDecimals) {
      const parts = value.toFixed(2).split('.');
      return `${group(parts[0])}${preferences.decimal_separator}${parts[1]}`;
    }
    return group(Math.round(value).toString());
  }, [group, preferences.decimal_separator]);

  /**
   * CR-7262: a quantity, which is not a plain number.
   *
   * `formatNumber` is binary — 0 or 2 decimals — so it cannot express one.
   * Quantities are stored as numeric(12,3), and rounding them for display is
   * not cosmetic: a supplier reading 56 against an order for 56.44 will
   * deliver 56. Up to three decimals, trailing zeros trimmed, so 56.440 reads
   * "56.44" and 56.000 reads "56" rather than "56.000".
   *
   * Grouping and separators come from the tenant's preferences, which is the
   * reason to put this here rather than write a fourth local helper: every
   * local one hard-coded 'en-GB', so a tenant using comma decimals was
   * already being shown the wrong separator.
   *
   * @param value - The quantity, as the API sends it (a numeric string), a
   *   number, or absent.
   * @returns The formatted quantity, or "-" when there is no value to show.
   *   Callers render a dash for an absent quantity everywhere it appears, so
   *   the fallback lives here instead of at twenty call sites.
   */
  const formatQuantity = useCallback((value: number | string | null | undefined): string => {
    if (value === null || value === undefined || value === '') return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (!Number.isFinite(num)) return '-';
    // Split before trimming: a plain `replace(/\.?0+$/, '')` also eats the
    // trailing zeros of a whole number, turning 560 into 56.
    const [whole, fraction = ''] = Math.abs(num).toFixed(3).split('.');
    const trimmed = fraction.replace(/0+$/, '');
    const sign = num < 0 ? '-' : '';
    return `${sign}${group(whole)}${trimmed ? preferences.decimal_separator + trimmed : ''}`;
  }, [group, preferences.decimal_separator]);

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        userPreferences,
        choices,
        exchangeRates,
        // Loading until the first load for this session has completed (see hasLoadedForSession).
        isLoading: isLoading || (isAuthenticated && !hasLoadedForSession),
        isUpdating,
        error,
        loadPreferences,
        updateUserPreferences,
        formatCurrency,
        formatCurrencyFromBase,
        convertFromBase,
        formatCompactCurrency,
        formatDate,
        formatDateTime,
        formatNumber,
        formatQuantity,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

/**
 * Accesses the effective user preferences (currency, timezone, date format) and formatting helpers.
 * Must be used within a {@link PreferencesProvider}.
 *
 * @returns Preferences state with formatCurrency, formatDate, formatNumber, and currency conversion helpers
 */
export const usePreferences = (): PreferencesContextType => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};

/**
 * Convenience hook that exposes only the formatting functions from PreferencesContext.
 * Use this in components that only need formatCurrency/formatDate/formatNumber
 * without the full preferences editing state.
 */
export const useFormatters = () => {
  const { formatCurrency, formatCurrencyFromBase, convertFromBase, formatCompactCurrency, formatDate, formatDateTime, formatNumber, formatQuantity, preferences, exchangeRates } = usePreferences();
  return { formatCurrency, formatCurrencyFromBase, convertFromBase, formatCompactCurrency, formatDate, formatDateTime, formatNumber, formatQuantity, preferences, exchangeRates };
};
